import { MarkingSchemeType } from '@/types/question.type'
import clsx from 'clsx'
import { Card } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { QuestionSchemaTabs } from '../constant'
import Answer from './QuestionSchema/Answer'
import MarkingScheme from './QuestionSchema/MarkingScheme'

const QuestionSchema = ({
  data,
  scheme,
  noBorder = false,
  isEditing = false,
  cantEditMark = false,
  isParagraph = true,
  onChange,
}: {
  isParagraph: boolean
  scheme: MarkingSchemeType[]
  data: { indicative_content: string; rubric: string } | undefined
  noBorder?: boolean
  isEditing?: boolean
  cantEditMark?: boolean
  onChange?: (updated: {
    indicative_content?: string
    rubric?: string
    scheme?: MarkingSchemeType[]
  }) => void
}) => {
  const isBlank = (value?: string) => (value ?? '').trim().length === 0
  const schemaTabs = [
    QuestionSchemaTabs.answer,
    QuestionSchemaTabs.marking_scheme,
  ]

  return (
    <Card
      className={clsx(
        noBorder ? 'border-none p-0 shadow-none' : 'mt-4 border-[#C2DEFF] p-4'
      )}
    >
      <Tabs defaultValue={QuestionSchemaTabs.answer} className='flex-col gap-3'>
        <TabsList className='gap-3 bg-transparent'>
          {schemaTabs.map((item: string) => (
            <TabsTrigger value={item} key={item} className='p-1 text-base'>
              {item}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={QuestionSchemaTabs.answer}>
          {isEditing && !cantEditMark && data ? (
            <textarea
              className={`min-h-[80px] w-full resize-y rounded-md border p-2 outline-none select-text ${
                isBlank(data.indicative_content)
                  ? 'border-red-500'
                  : 'border-[#C2DEFF]'
              }`}
              data-invalid={isBlank(data.indicative_content) ? 'true' : 'false'}
              rows={3}
              onMouseDown={(e) => {
                // Only stop propagation for specific interactions, not text selection
                const target = e.target as HTMLTextAreaElement
                if (target.selectionStart !== target.selectionEnd) {
                  return // User is selecting text, don't interfere
                }
                e.stopPropagation()
              }}
              onClick={(e) => {
                // Allow text selection clicks
                const target = e.target as HTMLTextAreaElement
                if (target.selectionStart !== target.selectionEnd) {
                  return // User is selecting text
                }
                e.stopPropagation()
              }}
              onFocus={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              value={data.indicative_content ?? ''}
              onChange={(e) =>
                onChange && onChange({ indicative_content: e.target.value })
              }
            />
          ) : (
            <Answer text={data?.indicative_content ?? ''} />
          )}
        </TabsContent>
        <TabsContent value={QuestionSchemaTabs.marking_scheme}>
          <MarkingScheme
            isParagraph={isParagraph}
            isEditing={isEditing}
            cantEditMark={cantEditMark}
            markingScheme={scheme}
            onChange={(updated) => onChange && onChange({ scheme: updated })}
          />
        </TabsContent>
        <TabsContent value={QuestionSchemaTabs.rubric}>
          {isEditing && data ? (
            <textarea
              className={`min-h-[80px] w-full resize-y rounded-md border border-[#C2DEFF] p-2 outline-none select-text`}
              data-invalid={false}
              rows={3}
              onMouseDown={(e) => {
                // Only stop propagation for specific interactions, not text selection
                const target = e.target as HTMLTextAreaElement
                if (target.selectionStart !== target.selectionEnd) {
                  return // User is selecting text, don't interfere
                }
                e.stopPropagation()
              }}
              onClick={(e) => {
                // Allow text selection clicks
                const target = e.target as HTMLTextAreaElement
                if (target.selectionStart !== target.selectionEnd) {
                  return // User is selecting text
                }
                e.stopPropagation()
              }}
              onFocus={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              value={data.rubric ?? ''}
              onChange={(e) => onChange && onChange({ rubric: e.target.value })}
            />
          ) : (
            <Answer text={data?.rubric ?? 'N/A'} />
          )}
        </TabsContent>
      </Tabs>
    </Card>
  )
}

export default QuestionSchema
